/**
 * UTM capture — Marketing attribution package (2026-07-17).
 *
 * Reads `utm_source, utm_medium, utm_campaign, utm_content, utm_term` from
 * the current URL at app boot and persists them into `sessionStorage` under
 * the single key `dreamable_utm` (JSON blob). Semantics:
 *
 *   - First-touch wins: if the key is already set within the session, a
 *     later capture is a no-op. This handles the case where the user lands
 *     on `/auth/signup?utm_...`, navigates to `/auth/login` inside the SPA,
 *     comes back — the original UTMs stay put.
 *   - Session-only: sessionStorage dies with the tab, so no cross-session
 *     identifier is created. This is the deliberate compliance choice —
 *     see below.
 *   - Safe no-op on every branch: Safari Private Mode throws on
 *     `sessionStorage.setItem`; SSR / server rendering has no `window`.
 *     Every operation is wrapped so attribution never breaks the app.
 *
 * ── Why sessionStorage, not localStorage ────────────────────────────────
 *
 * Cookiebot categorises persistent identifiers as statistics/marketing;
 * PostHog is deferred entirely until statistics consent (see
 * `posthog.ts`). Writing UTMs to localStorage before consent would break
 * the same posture. sessionStorage covers the realistic hop
 * (`dreamable.kids` → click → `app.dreamable.kids` signup within one tab
 * session) without creating a persistent identifier. Cross-session first-
 * touch attribution is still handled by PostHog's `$set_once` person-
 * property mechanism in `posthog.ts` once statistics consent is granted.
 *
 * ── Data flow ───────────────────────────────────────────────────────────
 *
 *   Webflow LP `dreamable.kids?utm_source=meta_ads&utm_medium=paid_social`
 *      │
 *      ▼   (webflow_utm_passthrough.js, commit 3/3)
 *   `app.dreamable.kids/auth/signup?utm_source=meta_ads&utm_medium=paid_social`
 *      │
 *      ▼   captureUtmsFromUrl() in main.tsx (this module)
 *   sessionStorage.dreamable_utm = '{"utm_source":"meta_ads",...}'
 *      │
 *      ▼   authStore.register() → readStoredUtms() → RegisterDto
 *   POST /api/auth/register { ..., utmSource, utmMedium, ... }
 *      │
 *      ▼   clearStoredUtms() on success
 *   sessionStorage.dreamable_utm removed
 *
 * The persisted user row now holds the UTMs on `User.utmSource` /
 * `.utmMedium` / etc. (backend Commit 1/3).
 */

export const UTM_STORAGE_KEY = 'dreamable_utm';

const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

type UtmParamKey = (typeof UTM_PARAM_KEYS)[number];

export type StoredUtms = Partial<Record<UtmParamKey, string>>;

/**
 * Camel-cased UTM fields ready for the RegisterDto / PATCH payload.
 * Present keys only — absent UTMs are omitted rather than set to `null`,
 * so the DTO's optional-field semantics stay clean.
 */
export interface UtmRegisterPayload {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

/** True when the runtime has sessionStorage available (jsdom/SSR/private-mode-safe). */
function hasSessionStorage(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

/**
 * Parse UTMs from the given URL query string. Returns an object with only
 * the present keys, or an empty object when none of the five are present.
 * Trims empty strings so `?utm_source=` doesn't produce `{ utm_source: '' }`.
 */
function parseUtmsFromSearch(search: string): StoredUtms {
  const out: StoredUtms = {};
  try {
    const params = new URLSearchParams(search);
    for (const key of UTM_PARAM_KEYS) {
      const value = params.get(key);
      if (value && value.trim().length > 0) {
        out[key] = value;
      }
    }
  } catch {
    // Ignore parse failures — attribution must never throw.
  }
  return out;
}

/**
 * Capture UTMs from `window.location.search` into sessionStorage. Idempotent
 * across the session — if the key is already set, this is a no-op
 * (first-touch-wins). Call once at app bootstrap in `main.tsx`, BEFORE
 * `initAnalytics()` so PostHog's in-memory `pendingUtms` and this
 * sessionStorage blob are seeded from the same URL parse.
 *
 * Never throws. Returns the captured UTMs (or the pre-existing ones, if any)
 * for testability; the return value is not used at the call site.
 */
export function captureUtmsFromUrl(): StoredUtms {
  if (!hasSessionStorage()) return {};

  // First-touch-wins: if we already have UTMs for this session, keep them.
  try {
    const existing = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    if (existing) {
      const parsed = JSON.parse(existing);
      if (parsed && typeof parsed === 'object') return parsed as StoredUtms;
    }
  } catch {
    // Corrupted blob — fall through and try to replace it with a fresh read.
  }

  const captured = parseUtmsFromSearch(window.location.search);
  if (Object.keys(captured).length === 0) return {};

  try {
    window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // Safari Private Mode throws on setItem — swallow and return the parsed
    // UTMs anyway so the current-tab flow still works (PostHog + register
    // both call this via readStoredUtms() through the same module).
  }
  return captured;
}

/**
 * Read UTMs from sessionStorage as camel-cased fields ready for the register
 * payload. Absent UTMs are omitted (undefined), NOT included with a `null`
 * value — matches the DTO's `?:` optional-field semantics.
 *
 * Never throws. Returns an empty object on any read failure (missing key,
 * corrupted JSON, jsdom without sessionStorage, etc.).
 */
export function readStoredUtms(): UtmRegisterPayload {
  if (!hasSessionStorage()) return {};
  try {
    const raw = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredUtms;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: UtmRegisterPayload = {};
    if (parsed.utm_source) out.utmSource = parsed.utm_source;
    if (parsed.utm_medium) out.utmMedium = parsed.utm_medium;
    if (parsed.utm_campaign) out.utmCampaign = parsed.utm_campaign;
    if (parsed.utm_content) out.utmContent = parsed.utm_content;
    if (parsed.utm_term) out.utmTerm = parsed.utm_term;
    return out;
  } catch {
    return {};
  }
}

/**
 * Delete the stored UTMs. Call after a successful register — the attribution
 * has landed on the user row and there's no reason to keep the blob around
 * (a second signup in the same tab would re-capture from the URL anyway).
 *
 * Never throws.
 */
export function clearStoredUtms(): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(UTM_STORAGE_KEY);
  } catch {
    // no-op
  }
}
